import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BookmarkNode, BrowserSettings, BrowserTab, ConsoleEntry, ErrorPageKind, InstalledExtension, TabGroup } from './types';
import { DEFAULT_SETTINGS, GROUP_COLORS } from './constants';
import { addHistory, loadBookmarks, loadSettings, loadTabs, saveBookmarks, saveSettings, saveTabs } from './lib/db';
import { createGroup, createTab, duplicateTab, goBack, goForward, navigateTab } from './lib/browserState';
import { faviconFor, internalPageFromUrl, isInternalUrl, nextRenderMode, normalizeInputToUrl, shouldUseNativeGameLaunch, toDisplayUrl } from './lib/urls';
import { uid } from './lib/ids';
import { savePageForOffline } from './lib/pageSnapshot';
import { TabStrip } from './components/TabStrip';
import { Toolbar } from './components/Toolbar';
import { BookmarksBar } from './components/BookmarksBar';
import { BrowserPane } from './components/BrowserPane';
import { InternalPage } from './components/InternalPage';
import { SiteInfoPanel } from './components/SiteInfoPanel';
import { TabSearchModal, Toast } from './components/Modals';

function removeBookmarksByUrl(node: BookmarkNode, url: string): BookmarkNode {
  if (!node.children) return node;
  return { ...node, children: node.children.filter((child) => child.type !== 'bookmark' || child.url !== url).map((child) => removeBookmarksByUrl(child, url)), updatedAt: Date.now() };
}

function hasBookmark(node: BookmarkNode, url: string): boolean {
  if (node.type === 'bookmark' && node.url === url) return true;
  return (node.children || []).some((child) => hasBookmark(child, url));
}

export default function App() {
  const [ready, setReady] = useState(false);
  const [settings, setSettingsState] = useState<BrowserSettings>(DEFAULT_SETTINGS);
  const [tabs, setTabs] = useState<BrowserTab[]>([]);
  const [groups, setGroups] = useState<TabGroup[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | undefined>();
  const [bookmarks, setBookmarksState] = useState<BookmarkNode>({ id: 'root', type: 'folder', title: 'Bookmarks', children: [], createdAt: Date.now(), updatedAt: Date.now() });
  const [extensions, setExtensions] = useState<InstalledExtension[]>([]);
  const [consoleEntries, setConsoleEntries] = useState<ConsoleEntry[]>([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [siteInfoOpen, setSiteInfoOpen] = useState(false);
  const [tabSearchOpen, setTabSearchOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(() => typeof document !== 'undefined' && !!document.fullscreenElement);
  const lastVisitRef = useRef<Record<string, string>>({});

  const activeTab = useMemo(() => tabs.find((tab) => tab.id === activeTabId) || tabs[0], [tabs, activeTabId]);

  useEffect(() => {
    let cancelled = false;
    const safetyTimer = window.setTimeout(() => {
      if (cancelled) return;
      console.warn('WebSurf initialization timed out — booting with defaults.');
      const fallbackTab = createTab(DEFAULT_SETTINGS.homepage, DEFAULT_SETTINGS);
      setSettingsState(DEFAULT_SETTINGS);
      setTabs([fallbackTab]);
      setGroups([]);
      setActiveTabId(fallbackTab.id);
      setReady(true);
    }, 4000);

    (async () => {
      let loadedSettings = DEFAULT_SETTINGS;
      let session: { tabs: BrowserTab[]; activeTabId?: string; groups: TabGroup[] } = { tabs: [], groups: [] };
      let loadedBookmarks: BookmarkNode | undefined;
      try {
        loadedSettings = await loadSettings();
      } catch (error) {
        console.warn('WebSurf: failed to load settings, using defaults.', error);
      }
      try {
        session = await loadTabs();
      } catch (error) {
        console.warn('WebSurf: failed to load saved tabs, starting fresh.', error);
      }
      try {
        loadedBookmarks = await loadBookmarks();
      } catch (error) {
        console.warn('WebSurf: failed to load bookmarks.', error);
      }
      if (cancelled) return;
      const initialTabs: BrowserTab[] = (session.tabs.length ? session.tabs : [createTab(loadedSettings.homepage, loadedSettings)]).map((tab) => shouldUseNativeGameLaunch(tab.url) ? { ...tab, renderMode: tab.renderMode === 'smart' ? ('smart' as const) : undefined, error: undefined } : tab);
      setSettingsState(loadedSettings);
      setTabs(initialTabs);
      setGroups(session.groups || []);
      setActiveTabId(session.activeTabId || initialTabs[0]?.id);
      if (loadedBookmarks) setBookmarksState(loadedBookmarks);
      setReady(true);
      window.clearTimeout(safetyTimer);
    })().catch((error) => {
      console.warn('WebSurf: initialization error, booting with defaults.', error);
      if (cancelled) return;
      const fallbackTab = createTab(DEFAULT_SETTINGS.homepage, DEFAULT_SETTINGS);
      setSettingsState(DEFAULT_SETTINGS);
      setTabs([fallbackTab]);
      setActiveTabId(fallbackTab.id);
      setReady(true);
      window.clearTimeout(safetyTimer);
    });

    return () => { cancelled = true; window.clearTimeout(safetyTimer); };
  }, []);

  useEffect(() => {
    if (!ready) return;
    saveTabs({ tabs, activeTabId, groups }).catch((error) => console.warn('Failed to save session', error));
  }, [ready, tabs, activeTabId, groups]);

  useEffect(() => {
    const onlineHandler = () => setOnline(true);
    const offlineHandler = () => setOnline(false);
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);
    return () => { window.removeEventListener('online', onlineHandler); window.removeEventListener('offline', offlineHandler); };
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
      }
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'Could not toggle fullscreen.');
    }
  }, []);

  const updateSettings = useCallback((next: BrowserSettings) => {
    setSettingsState(next);
    saveSettings(next).catch((error) => console.warn('Failed to save settings', error));
  }, []);

  const updateBookmarks = useCallback((next: BookmarkNode) => {
    setBookmarksState(next);
    saveBookmarks(next).catch((error) => console.warn('Failed to save bookmarks', error));
  }, []);

  const openNewTab = useCallback((url?: string) => {
    const target = url || (settings.openHomepageOnNewTab ? settings.homepage : 'websurf://home');
    const tab = createTab(target, settings, { status: isInternalUrl(target) ? 'complete' : 'loading' });
    setTabs((current) => [...current, tab]);
    setActiveTabId(tab.id);
  }, [settings]);

  const navigateByTabId = useCallback((tabId: string, input: string) => {
    const target = normalizeInputToUrl(input, settings);
    setTabs((current) => current.map((tab) => tab.id === tabId ? navigateTab(tab, target) : tab));
    setActiveTabId(tabId);
  }, [settings]);

  const navigateActive = useCallback((input: string) => {
    if (!activeTab) return;
    navigateByTabId(activeTab.id, input);
  }, [activeTab, navigateByTabId]);

  const closeTab = useCallback((tabId: string) => {
    setTabs((current) => {
      if (current.length <= 1) {
        const replacement = createTab(settings.homepage, settings, { status: 'complete' });
        setActiveTabId(replacement.id);
        return [replacement];
      }
      const index = current.findIndex((tab) => tab.id === tabId);
      const next = current.filter((tab) => tab.id !== tabId);
      if (tabId === activeTabId) setActiveTabId(next[Math.max(0, index - 1)]?.id || next[0]?.id);
      return next;
    });
  }, [activeTabId, settings]);

  const reloadActive = useCallback(() => {
    if (!activeTab) return;
    setTabs((current) => current.map((tab) => tab.id === activeTab.id ? { ...tab, status: isInternalUrl(tab.url) ? 'complete' : 'loading', error: undefined, offlineSnapshotId: undefined, reloadKey: (tab.reloadKey || 0) + 1 } : tab));
  }, [activeTab]);

  const updateActiveTab = useCallback((updater: (tab: BrowserTab) => BrowserTab) => {
    if (!activeTab) return;
    setTabs((current) => current.map((tab) => tab.id === activeTab.id ? updater(tab) : tab));
  }, [activeTab]);

  const setTabError = useCallback((tabId: string, kind: ErrorPageKind) => {
    setTabs((current) => current.map((tab) => tab.id === tabId ? { ...tab, status: kind === 'offline' ? 'offline' : 'error', error: kind } : tab));
  }, []);

  useEffect(() => {
    function handler(event: MessageEvent) {
      const data = event.data || {};
      if (data.type === 'websurf:home') {
        if (activeTab) navigateByTabId(activeTab.id, 'websurf://home');
        return;
      }
      if (data.source !== 'websurf-frame') return;
      const tabId = data.tabId || activeTabId;
      if (!tabId) return;
      if (data.type === 'websurf:loaded') {
        const url = data.url || tabs.find((tab) => tab.id === tabId)?.url || '';
        const title = data.title || toDisplayUrl(url);
        const favicon = data.favicon || faviconFor(url);
        setTabs((current) => current.map((tab) => tab.id === tabId ? { ...tab, url: url || tab.url, status: 'complete', title, favicon, error: undefined, displayUrl: toDisplayUrl(url || tab.url) } : tab));
        if (url && !isInternalUrl(url) && lastVisitRef.current[tabId] !== `${url}:${title}`) {
          lastVisitRef.current[tabId] = `${url}:${title}`;
          addHistory({ url, title, favicon }).catch(() => undefined);
        }
      }
      if (data.type === 'websurf:navigate' && data.url) navigateByTabId(tabId, data.url);
      if (data.type === 'websurf:new-tab' && data.url) openNewTab(data.url);
      if (data.type === 'websurf:proxy-error') setTabError(tabId, (data.reason || 'error') as ErrorPageKind);
      if (data.type === 'websurf:console') {
        setConsoleEntries((current) => [...current.slice(-250), { id: uid('console'), tabId, level: data.level || 'log', message: data.message || '', createdAt: Date.now() }]);
      }
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [activeTab, activeTabId, navigateByTabId, openNewTab, setTabError, tabs]);

  useEffect(() => {
    function keydown(event: KeyboardEvent) {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;
      const key = event.key.toLowerCase();
      if (key === 't') { event.preventDefault(); openNewTab(); }
      if (key === 'w' && activeTab) { event.preventDefault(); closeTab(activeTab.id); }
      if (key === 'r') { event.preventDefault(); reloadActive(); }
      if (key === 'l') { event.preventDefault(); (document.querySelector('.omnibox input') as HTMLInputElement | null)?.select(); }
      if (key === 'f' && event.shiftKey) { event.preventDefault(); setTabSearchOpen(true); }
      if (key === '=' || key === '+') { event.preventDefault(); updateActiveTab((tab) => ({ ...tab, zoom: Math.min(2, Number((tab.zoom + 0.1).toFixed(2))) })); }
      if (key === '-') { event.preventDefault(); updateActiveTab((tab) => ({ ...tab, zoom: Math.max(0.5, Number((tab.zoom - 0.1).toFixed(2))) })); }
      if (key === '0') { event.preventDefault(); updateActiveTab((tab) => ({ ...tab, zoom: 1 })); }
      if (event.key === 'F11') { event.preventDefault(); toggleFullscreen(); }
    }
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  }, [activeTab, closeTab, openNewTab, reloadActive, updateActiveTab, toggleFullscreen]);

  function toggleBookmark() {
    if (!activeTab) return;
    if (hasBookmark(bookmarks, activeTab.url)) {
      updateBookmarks(removeBookmarksByUrl(bookmarks, activeTab.url));
      setToast('Bookmark removed.');
    } else {
      const now = Date.now();
      updateBookmarks({ ...bookmarks, children: [...(bookmarks.children || []), { id: uid('bm'), type: 'bookmark', title: activeTab.title || activeTab.displayUrl, url: activeTab.url, favicon: activeTab.favicon, createdAt: now, updatedAt: now }], updatedAt: now });
      setToast('Bookmark added.');
    }
  }

  async function makeOffline() {
    if (!activeTab || isInternalUrl(activeTab.url)) return;
    setToast('Saving page for offline use…');
    try {
      const record = await savePageForOffline(activeTab.url);
      setTabs((current) => current.map((tab) => tab.id === activeTab.id ? { ...tab, offlineSnapshotId: record.id } : tab));
      setToast(`Saved offline copy: ${record.title}`);
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'Offline save failed.');
    }
  }

  function createTabGroup(tabId: string) {
    const name = prompt('Tab group name', 'Group');
    if (!name) return;
    const group = createGroup(name, GROUP_COLORS[groups.length % GROUP_COLORS.length]);
    setGroups((current) => [...current, group]);
    setTabs((current) => current.map((tab) => tab.id === tabId ? { ...tab, groupId: group.id } : tab));
  }

  const internalPage = internalPageFromUrl(activeTab?.url || 'websurf://home') || 'home';

  if (!ready || !activeTab) return (
    <div className="boot-screen">
      <img src="/icons/mark-circle.png" alt="" />
      <span>Starting WebSurf Search Browser…</span>
      <button
        type="button"
        style={{ marginTop: 14, border: '1px solid rgba(67,66,66,.18)', background: '#fff', color: '#434242', borderRadius: 999, padding: '8px 14px', cursor: 'pointer', font: 'inherit' }}
        onClick={() => {
          try { indexedDB.deleteDatabase('websurf-db'); } catch {}
          try { localStorage.clear(); } catch {}
          location.reload();
        }}
      >
        Stuck? Reset WebSurf data
      </button>
    </div>
  );

  return (
    <div className={settings.reducedMotion ? 'app reduced-motion' : 'app'}>
      <TabStrip
        tabs={tabs}
        groups={groups}
        activeTabId={activeTab.id}
        onSelect={setActiveTabId}
        onClose={closeTab}
        onNewTab={() => openNewTab()}
        onDuplicate={(id) => setTabs((current) => {
          const source = current.find((tab) => tab.id === id);
          if (!source) return current;
          const duplicate = duplicateTab(source);
          setActiveTabId(duplicate.id);
          return [...current, duplicate];
        })}
        onTogglePin={(id) => setTabs((current) => current.map((tab) => tab.id === id ? { ...tab, pinned: !tab.pinned } : tab))}
        onToggleSearch={() => setTabSearchOpen(true)}
        onCreateGroup={createTabGroup}
      />
      <Toolbar
        tab={activeTab}
        settings={settings}
        bookmarks={bookmarks}
        online={online}
        onNavigate={navigateActive}
        onBack={() => setTabs((current) => current.map((tab) => tab.id === activeTab.id ? goBack(tab) : tab))}
        onForward={() => setTabs((current) => current.map((tab) => tab.id === activeTab.id ? goForward(tab) : tab))}
        onReload={reloadActive}
        onHome={() => navigateActive(settings.homepage)}
        onToggleBookmark={toggleBookmark}
        onOpenSiteInfo={() => setSiteInfoOpen(true)}
        onSaveOffline={makeOffline}
        onReadingMode={() => updateActiveTab((tab) => ({ ...tab, viewMode: tab.viewMode === 'reading' ? 'browser' : 'reading' }))}
        onDevTools={() => updateActiveTab((tab) => ({ ...tab, viewMode: tab.viewMode === 'devtools' ? 'browser' : 'devtools' }))}
        onTranslate={(target) => updateSettings({ ...settings, translateTarget: target })}
        onOpenInternal={navigateActive}
        onCycleRenderMode={() => updateActiveTab((tab) => ({ ...tab, renderMode: nextRenderMode(tab.renderMode || settings.defaultRenderMode || 'smart', tab.url), error: undefined, status: isInternalUrl(tab.url) ? 'complete' : 'loading', reloadKey: (tab.reloadKey || 0) + 1 }))}
        onToggleFullscreen={toggleFullscreen}
        isFullscreen={isFullscreen}
      />
      <BookmarksBar root={bookmarks} visible={settings.showBookmarksBar} onOpen={navigateActive} onOpenManager={() => navigateActive('websurf://bookmarks')} />
      <main className="viewport">
        <BrowserPane
          tab={activeTab}
          settings={settings}
          extensions={extensions}
          consoleEntries={consoleEntries}
          translateTarget={settings.translateTarget}
          onLoaded={(tabId, payload) => {
            setTabs((current) => current.map((tab) => tab.id === tabId ? { ...tab, status: 'complete', title: payload.title || tab.title, favicon: payload.favicon || tab.favicon } : tab));
          }}
          onNavigate={navigateByTabId}
          onNewTab={openNewTab}
          onError={setTabError}
          onConsole={(entry) => setConsoleEntries((current) => [...current, { ...entry, id: uid('console'), createdAt: Date.now() }])}
          onUseOfflineSnapshot={(tabId, snapshotId) => setTabs((current) => current.map((tab) => tab.id === tabId ? { ...tab, status: 'offline', offlineSnapshotId: snapshotId, error: undefined } : tab))}
          onRetry={(tabId) => setTabs((current) => current.map((tab) => tab.id === tabId ? { ...tab, status: 'loading', error: undefined, offlineSnapshotId: undefined, reloadKey: (tab.reloadKey || 0) + 1 } : tab))}
          onSetRenderMode={(tabId, mode) => setTabs((current) => current.map((tab) => tab.id === tabId ? { ...tab, renderMode: mode, error: undefined, status: isInternalUrl(tab.url) ? 'complete' : 'loading', reloadKey: (tab.reloadKey || 0) + 1 } : tab))}
          renderInternal={() => <InternalPage page={internalPage} currentUrl={activeTab.url} bookmarks={bookmarks} settings={settings} extensions={extensions} onBookmarksChange={updateBookmarks} onSettingsChange={updateSettings} onExtensionsChange={setExtensions} onOpen={navigateActive} />}
        />
      </main>
      {siteInfoOpen && <SiteInfoPanel url={activeTab.url} onClose={() => setSiteInfoOpen(false)} />}
      {tabSearchOpen && <TabSearchModal tabs={tabs} onSelect={setActiveTabId} onClose={() => setTabSearchOpen(false)} />}
      <Toast message={toast} onClose={() => setToast('')} />
    </div>
  );
}
